'use client';

import type { StaticImageData } from 'next/image';
import { useTranslations } from 'next-intl';
import { type DragEvent, forwardRef, type ReactNode } from 'react';
import Img2ReferenceEnImage from '@/app/assets/imgs/char2v/img2reference-en.png';
import Img2ReferenceZhImage from '@/app/assets/imgs/char2v/img2reference-zh.png';
import { BaseLineAudioPlay } from '@/app/components/icons/BaseLineAudioPlay';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { BaseLineReplace } from '@/app/components/icons/BaseLineReplace';
import { CutTailoring1 } from '@/app/components/icons/CutTailoring1';
import { Loading } from '@/app/components/icons/Loading';
import { ProductLineElement } from '@/app/components/icons/ProductLineElement';
import { ProductLineTransform } from '@/app/components/icons/ProductLineTransform';
import { ProductLineVideoReference } from '@/app/components/icons/ProductLineVideoReference';
import { ProductView } from '@/app/components/icons/ProductView';
import { SelectObject } from '@/app/components/icons/SelectObject';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { ImageOperationDialog } from '@/app/components/shared/ImageOperationDialog';
import { ImageUploader } from '@/app/components/shared/ImageUploader';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { MediaPromptPreview } from '@/app/components/shared/MediaPromptPreview';
import { MediaWithMaskBg } from '@/app/components/shared/MediaWithMaskBg';
import { ReferenceCard } from '@/app/components/shared/ReferenceCard';
import { TextElementCover } from '@/app/components/shared/TextElementCover';
import { GLOBAL_LANGUAGES } from '@/app/constants';
import type { ElementType, ImagePrompt, ImageUploadFile, SupportedLanguage, UploadImageType } from '@/app/types';
import { cn } from '@/app/utils';
import { resizeSourceImage } from '@/app/utils/client';
import { useLocale } from '@/i18n/routing';

const generateLocalizedImages = (): Record<SupportedLanguage, StaticImageData> => {
  const dynamicConfig = GLOBAL_LANGUAGES.filter((lang) => lang !== 'zh').reduce(
    (acc, lang) => {
      acc[lang] = Img2ReferenceEnImage;
      return acc;
    },
    {} as Record<string, StaticImageData>,
  );

  return {
    zh: Img2ReferenceZhImage,
    ...dynamicConfig,
  } as Record<SupportedLanguage, StaticImageData>;
};

const LOCALIZED_IMAGES: Record<'img2Reference', Record<SupportedLanguage, StaticImageData>> = {
  img2Reference: generateLocalizedImages(),
};

type DragHandlers = {
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
  onDragOver?: (e: DragEvent<HTMLDivElement>) => void;
  onDropCapture?: (e: DragEvent<HTMLDivElement>) => void;
};

export interface ReferencePromptAddCardProps {
  imageSlot?: ReactNode;
  elementSlot?: ReactNode;
  audioSlot?: ReactNode;
  className?: string;
}

export function ReferencePromptAddCard({ imageSlot, elementSlot, audioSlot, className }: ReferencePromptAddCardProps) {
  const hasImage = Boolean(imageSlot);
  const hasElement = Boolean(elementSlot);
  const hasAudio = Boolean(audioSlot);

  return (
    <div
      aria-label="upload"
      className={cn(
        'flex h-24 w-full flex-col overflow-hidden rounded-8 border border-system-white12 bg-system-white08',
        className,
      )}
    >
      {imageSlot && (
        <div className="flex min-h-0 flex-1 items-stretch overflow-hidden [&_span]:whitespace-nowrap">{imageSlot}</div>
      )}
      {hasImage && (hasElement || hasAudio) && <div className="h-[1px] w-full bg-system-white08" />}
      {elementSlot && (
        <div className="flex min-h-0 flex-1 items-stretch overflow-hidden [&_span]:whitespace-nowrap">
          {elementSlot}
        </div>
      )}
      {hasElement && hasAudio && <div className="h-[1px] w-full bg-system-white08" />}
      {audioSlot && (
        <div className="flex min-h-0 flex-1 items-stretch overflow-hidden [&_span]:whitespace-nowrap">{audioSlot}</div>
      )}
    </div>
  );
}

export interface ReferencePromptImageCardProps extends DragHandlers {
  id: string;
  prompt: ImagePrompt | null;
  name: string;
  indexLabel: ReactNode;
  deleteButton?: ReactNode;
  maxImageSize: number;
  replaceImage: (file: ImageUploadFile) => UploadImageType | null;
  allowTempMode: boolean;
  isCropLoading?: boolean;
  onCrop: () => void;
  onRemove: () => void;
  onCreateElement: () => void;
  contentClassName?: string;
}

export const ReferencePromptImageCard = forwardRef<HTMLDivElement, ReferencePromptImageCardProps>(
  (
    {
      id,
      prompt,
      name,
      indexLabel,
      deleteButton,
      maxImageSize,
      replaceImage,
      allowTempMode,
      isCropLoading = false,
      onCrop,
      onRemove,
      onCreateElement,
      contentClassName,
      onDragStart,
      onDragOver,
      onDropCapture,
    },
    ref,
  ) => {
    const t = useTranslations();
    const locale = useLocale();

    return (
      <ReferenceCard
        key={id}
        id={id}
        ref={ref}
        className="h-24"
        draggable={true}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDropCapture={onDropCapture}
        footer={<span className="opacity-60">{indexLabel}</span>}
        deleteButton={prompt ? deleteButton : undefined}
        hoverOperations={
          prompt
            ? [
                <ImageUploader
                  key="replace"
                  maxCount={1}
                  getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                  onChange={replaceImage}
                  allowTempMode={allowTempMode}
                >
                  <BaseLineReplace className="text-system-white text-xl" />
                </ImageUploader>,
                isCropLoading ? (
                  <Loading key="select" className="animate-spin text-system-white text-xl" />
                ) : (
                  <CutTailoring1 key="select" className="cursor-pointer text-system-white text-xl" onClick={onCrop} />
                ),
              ]
            : undefined
        }
        topRightSlot={
          prompt ? (
            <ImageOperationDialog
              image={prompt}
              triggerClassName="hidden cursor-pointer items-center justify-center rounded-4 bg-[rgba(0,0,0,.5)] p-[1px] text-system-white text-xl sm:flex w-6 h-6"
              handleRemoveImage={onRemove}
              operations={[
                {
                  key: 'select',
                  icon: isCropLoading ? <Loading className="animate-spin" /> : <SelectObject />,
                  text: t('create.createForm.upload.crop'),
                  onClick: onCrop,
                  disabled: isCropLoading,
                },
              ]}
            />
          ) : undefined
        }
        expandedFooter={
          prompt
            ? {
                left: name,
                right: (
                  <CustomTooltip
                    content={
                      <>
                        <div className="flex w-fit items-center gap-2 rounded-[100px] bg-[rgba(58,162,255,0.2)] px-2 py-1">
                          <ProductLineTransform className="text-[#4B96FF] text-sm" />
                          <span className="text-[#4B96FF] text-sm">
                            {t('create.char2VideoForm.element.addToElement')}
                          </span>
                        </div>
                        <p className="my-3 font-medium text-base text-white">
                          {t('create.char2VideoForm.element.addToElementTip')}
                        </p>
                        <ImageWithPlaceholder
                          src={
                            LOCALIZED_IMAGES.img2Reference[locale as keyof typeof LOCALIZED_IMAGES.img2Reference].src
                          }
                          alt=""
                          className="h-[160px] w-[296px] object-cover"
                        />
                      </>
                    }
                    variant="extend"
                    contentClassName="w-[328px] p-4"
                    sideOffset={8}
                    disableHoverableContent={false}
                    align="start"
                    side="bottom"
                    trigger="hover"
                  >
                    <ProductLineTransform
                      className="text-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateElement();
                      }}
                    />
                  </CustomTooltip>
                ),
              }
            : undefined
        }
        contentClassName={contentClassName}
      >
        {prompt ? (
          <MediaWithMaskBg
            src={prompt.selectedImagePreview || prompt.sourceImagePreview}
            maskSrc={prompt.selectedImagePreview || prompt.sourceImagePreview}
            alt={name}
            className="size-full rounded-8 group-hover:rounded-b-none"
            type="image"
            disableDrag={true}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Loading className="animate-spin text-2xl" />
          </div>
        )}
      </ReferenceCard>
    );
  },
);

ReferencePromptImageCard.displayName = 'ReferencePromptImageCard';

export interface ReferencePromptElementCardProps extends DragHandlers {
  id: string;
  element: ElementType;
  name: string;
  deleteButton?: ReactNode;
  showVideoIcon?: boolean;
  showActions?: boolean;
  showTopRightSlot?: boolean;
  canEdit?: boolean;
  onView: () => void;
  onEdit: () => void;
}

export function ReferencePromptElementCard({
  id,
  element,
  name,
  deleteButton,
  showVideoIcon = false,
  showActions = true,
  showTopRightSlot = true,
  canEdit = false,
  onView,
  onEdit,
  onDragStart,
  onDragOver,
  onDropCapture,
}: ReferencePromptElementCardProps) {
  const optionalAssets =
    element.modality === 'image' || element.modality === 'video' ? element.optionalAssets : undefined;
  const isTextModality = element.modality === 'text';
  const FooterIcon = showVideoIcon ? ProductLineVideoReference : ProductLineElement;

  return (
    <ReferenceCard
      key={id}
      id={id}
      className="h-24"
      draggable={true}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDropCapture={onDropCapture}
      footer={
        <>
          <FooterIcon className="size-[10px] flex-shrink-0 text-system-blue02" />
          <span className="max-w-[60px] truncate">{name}</span>
        </>
      }
      expandedFooter={{
        left: (
          <div className="flex items-center gap-1">
            <FooterIcon className="size-[10px] flex-shrink-0 text-system-blue02" />
            <span className="max-w-[60px] truncate">{name}</span>
          </div>
        ),
      }}
      deleteButton={deleteButton}
      hoverOperations={
        showActions
          ? [
              canEdit && (
                <BaseLineEdit key="edit" className="cursor-pointer text-system-white text-xl" onClick={onEdit} />
              ),
              <ProductView key="view" className="cursor-pointer text-system-white text-xl" onClick={onView} />,
            ].filter(Boolean)
          : undefined
      }
      topRightSlot={
        showTopRightSlot ? (
          <div
            className="hidden h-6 w-6 cursor-pointer items-center justify-center rounded-4 bg-[rgba(0,0,0,.5)] p-[1px] text-system-white text-xl sm:flex"
            onClick={onView}
          >
            <BaseLineEdit className="size-4" />
          </div>
        ) : undefined
      }
      footerClassName="flex items-center gap-1"
    >
      {isTextModality ? (
        <TextElementCover name={name} className="rounded-8 text-xs group-hover:rounded-b-none" />
      ) : (
        <MediaPromptPreview
          prompt={optionalAssets?.[0] || null}
          className="rounded-8 group-hover:rounded-b-none"
          onClick={onView}
        />
      )}
    </ReferenceCard>
  );
}

export interface ReferencePromptForbiddenCardProps {
  id: string;
  name: string;
  deleteButton?: ReactNode;
}

export function ReferencePromptForbiddenCard({ id, name, deleteButton }: ReferencePromptForbiddenCardProps) {
  const t = useTranslations();

  return (
    <CustomTooltip
      key={id}
      content={t('create.char2VideoForm.elementForbidden.tooltip')}
      sideOffset={4}
      contentClassName="bg-[#525A69]"
      arrowClassName="text-[#525A69]"
      disableHoverableContent={false}
      trigger="hover"
    >
      <ReferenceCard id={id} className="h-24 bg-[#323845]" footer={name} deleteButton={deleteButton}>
        <div className="flex h-full w-full items-center justify-center">
          <BaseLineExclamation className="text-2xl text-system-white36" />
        </div>
      </ReferenceCard>
    </CustomTooltip>
  );
}

export interface ReferencePromptAudioCardProps extends DragHandlers {
  id: string;
  name: string;
  deleteButton?: ReactNode;
  onPlay?: () => void;
  onReplace?: () => void;
  replacementTrigger?: ReactNode;
}

export interface ReferencePromptUploadingCardProps {
  id: string;
  label: string;
}

export function ReferencePromptUploadingCard({ id, label }: ReferencePromptUploadingCardProps) {
  return (
    <ReferenceCard id={id} className="h-24" contentClassName="bg-system-white08">
      <div className="flex h-full w-full flex-col items-center justify-center gap-2">
        <Loading className="animate-spin text-2xl text-system-white64" />
        <div className="text-system-white48 text-xs">{label}</div>
      </div>
    </ReferenceCard>
  );
}

export function ReferencePromptAudioCard({
  id,
  name,
  deleteButton,
  onPlay,
  onReplace,
  replacementTrigger,
  onDragStart,
  onDragOver,
  onDropCapture,
}: ReferencePromptAudioCardProps) {
  return (
    <ReferenceCard
      key={id}
      id={id}
      className="h-24"
      draggable={true}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDropCapture={onDropCapture}
      footer={<span className="max-w-[60px] truncate">{name}</span>}
      footerClassName="flex items-center"
      expandedFooter={{ left: <span className="block max-w-[60px] truncate">{name}</span> }}
      deleteButton={deleteButton}
      hoverOperations={[
        replacementTrigger ??
          (onReplace ? (
            <BaseLineReplace key="replace" className="cursor-pointer text-system-white text-xl" onClick={onReplace} />
          ) : null),
        onPlay ? (
          <BaseLineAudioPlay key="play" className="cursor-pointer text-system-white text-xl" onClick={onPlay} />
        ) : null,
      ].filter(Boolean)}
      contentClassName="border-system-white12 bg-system-white08"
    >
      <div className="flex h-full w-full items-center justify-center">
        <BaseLineMusic className="size-7 text-system-white64" />
      </div>
    </ReferenceCard>
  );
}
